import { useState } from 'react';
import CashDrawerStatusIndicator from './CashDrawerStatusIndicator';
import DrawerDetailsModal from './DrawerDetailsModal';
import NoSaleModal from './NoSaleModal';
import OpenDrawerModal from './OpenDrawerModal';
import PaidInOutModal from './PaidInOutModal';
import ChangeCashierModal from './ChangeCashierModal';
import ClosingBalanceModal from './ClosingBalanceModal';
import { appendDrawerEvent, getDrawerDetailsForToday } from '../../../utils/drawerLedger';
import { toLocalISOString } from '../../../utils/dateUtils';
import { posApi } from '../../../api/pos';
import { unwrapResponse } from '../../../api/client';

export default function Header() {
  const [showDrawerDetails, setShowDrawerDetails] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [apiEvents, setApiEvents] = useState<any[]>([]);
  const [showNoSale, setShowNoSale] = useState(false);
  const [showOpenDrawer, setShowOpenDrawer] = useState(false);
  const [showPaidInOut, setShowPaidInOut] = useState(false);
  const [paidInOutType, setPaidInOutType] = useState<'in' | 'out'>('in');
  const [paidInOutBalance, setPaidInOutBalance] = useState(0);
  const [showChangeCashier, setShowChangeCashier] = useState(false);
  const [showClosingBalance, setShowClosingBalance] = useState(false);

  const getUserFullName = () => {
    try {
      const profile = JSON.parse(localStorage.getItem('clientBusinessProfile') || '{}');
      return profile?.userName || 'Cashier';
    } catch {
      return 'Cashier';
    }
  };
  const [currentCashier, setCurrentCashier] = useState(getUserFullName());

  const getBusinessProfileName = () => {
    try {
      const selectedProfile = JSON.parse(localStorage.getItem('biznifyBusinessProfile') || '{}');
      const clientProfile = JSON.parse(localStorage.getItem('clientBusinessProfile') || '{}');
      return (
        selectedProfile?.name ||
        clientProfile?.businessProfile?.name ||
        clientProfile?.businessName ||
        'RetailPro POS'
      );
    } catch {
      return 'RetailPro POS';
    }
  };
  const businessProfileName = getBusinessProfileName();

  const getLogoUrl = () => {
    try {
      const details = JSON.parse(localStorage.getItem('businessDetails') || '{}');
      return details?.imagePath || details?.data?.imagePath || null;
    } catch {
      return null;
    }
  };
  const logoUrl = getLogoUrl();

  const getOutletAndCounterInfo = () => {
    try {
      const checkedInInfo = JSON.parse(localStorage.getItem('counterCheckedInInfo') || '{}');
      const outlet = JSON.parse(localStorage.getItem('outlet') || '{}');
      return {
        counterName: checkedInInfo?.counterName || 'Lane',
        outletName: checkedInInfo?.outletName || outlet?.name || 'Outlet',
      };
    } catch {
      return {
        counterName: 'Lane',
        outletName: 'Outlet',
      };
    }
  };
  const { counterName, outletName } = getOutletAndCounterInfo();

  const toNum = (value: unknown, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const handleViewDrawerDetails = async () => {
    setShowDrawerDetails(true);
    setSessionLoading(true);
    setApiEvents([]);
    try {
      const counter = JSON.parse(localStorage.getItem('counter') || '{}');
      const counterId = Number(counter?.id ?? counter?.counterId ?? 0);
      if (!counterId) return;

      // GetAllCashMovements disabled for now — may be revisited in the future.
      // Falls back to local drawer events (see `events` from getDrawerDetailsForToday) below.
      const [sessionRes] = await Promise.allSettled([
        posApi.getLastCounterSession(counterId),
        // (async () => {
        //   const counterSession = JSON.parse(localStorage.getItem('counterSession') || '{}');
        //   const counterSessionId = Number(
        //     counterSession?.id ?? counterSession?.counterSessionId ?? counterSession?.sessionId ?? 0
        //   );
        //   if (!counterSessionId) return null;
        //   return posApi.getAllCashMovements(counterSessionId);
        // })(),
      ]);

      if (sessionRes.status === 'fulfilled') {
        setSessionData(unwrapResponse<any>(sessionRes.value));
      }
      // if (movementsRes.status === 'fulfilled' && movementsRes.value) {
      //   const movements = unwrapResponse<any[]>(movementsRes.value);
      //   setApiEvents(Array.isArray(movements) ? movements : []);
      // }
    } catch {
      // fall back to local data
    } finally {
      setSessionLoading(false);
    }
  };

  // Local fallbacks (same as ClosingBalanceModal)
  const openingBalanceData = JSON.parse(sessionStorage.getItem('posOpeningBalance') || '{}');
  const localOpeningBalance = parseFloat(openingBalanceData.amount) || 0;
  const paidInOutTransactions = JSON.parse(sessionStorage.getItem('paidInOutTransactions') || '[]');
  const localPaidIn = paidInOutTransactions.filter((t: any) => t.type === 'in').reduce((sum: number, t: any) => sum + t.amount, 0);
  const localPaidOut = paidInOutTransactions.filter((t: any) => t.type === 'out').reduce((sum: number, t: any) => sum + t.amount, 0);
  const completedSales = JSON.parse(sessionStorage.getItem('completedSales') || '[]');
  const localCashSales = completedSales.reduce((sum: number, sale: any) => {
    const cash = (sale.paymentMethods || []).filter((p: any) => p.method === 'Cash').reduce((t: number, p: any) => t + p.amount, 0);
    return sum + cash;
  }, 0);
  const localCashRefunds = completedSales.reduce((sum: number, sale: any) => {
    if (sale.total < 0) {
      const cash = (sale.paymentMethods || []).filter((p: any) => p.method === 'Cash').reduce((t: number, p: any) => t + Math.abs(p.amount), 0);
      return sum + cash;
    }
    return sum;
  }, 0);

  const sessionFields = {
    openingCash: toNum(sessionData?.openingCash, localOpeningBalance),
    salesAmount: toNum(sessionData?.salesAmount, localCashSales),
    cashIn: toNum(sessionData?.cashIn, localPaidIn),
    cashOut: toNum(sessionData?.cashOut, localPaidOut),
    receivedCash: toNum(sessionData?.receivedCash, 0),
    receivedBankAmount: toNum(sessionData?.receivedBankAmount, 0),
    salesReturnAmount: toNum(sessionData?.salesReturnAmount, localCashRefunds),
    salesRefundAmount: toNum(sessionData?.salesRefundAmount, 0),
    adjustedCreditAmount: toNum(sessionData?.adjustedCreditAmount, 0),
  };

  const fetchClosingCashBalance = async () => {
    try {
      const counter = JSON.parse(localStorage.getItem('counter') || '{}');
      const counterId = Number(counter?.id ?? counter?.counterId ?? 0);
      if (!counterId) return;
      const res = await posApi.getLastCounterSession(counterId);
      const session = unwrapResponse<any>(res);
      const closing =
        toNum(session?.openingCash) +
        toNum(session?.salesAmount) +
        toNum(session?.cashIn) -
        toNum(session?.cashOut) -
        toNum(session?.receivedBankAmount) -
        toNum(session?.salesReturnAmount);
      setPaidInOutBalance(closing);
    } catch {
      // keep previous value
    }
  };

  const handlePaidIn = async () => {
    setPaidInOutType('in');
    await fetchClosingCashBalance();
    setShowPaidInOut(true);
  };

  const handlePaidOut = async () => {
    setPaidInOutType('out');
    await fetchClosingCashBalance();
    setShowPaidInOut(true);
  };

  const handleCheckOut = () => {
    setShowClosingBalance(true);
  };

  const handleCashierChange = (cashierName: string) => {
    setCurrentCashier(cashierName);
    setShowChangeCashier(false);
  };

  const handlePaidInOutConfirm = (amount: number, reason: string, note: string) => {
    // Save the paid in/out transaction
    const transaction = {
      type: paidInOutType,
      amount,
      reason,
      note,
      timestamp: toLocalISOString(),
      cashier: currentCashier,
    };

    // Store in session storage
    const existingTransactions = JSON.parse(sessionStorage.getItem('paidInOutTransactions') || '[]');
    existingTransactions.push(transaction);
    sessionStorage.setItem('paidInOutTransactions', JSON.stringify(existingTransactions));

    appendDrawerEvent({
      type: paidInOutType === 'in' ? 'paid_in' : 'paid_out',
      amount,
      reason,
      note,
      user: currentCashier,
      role: 'Cashier',
    });
    
    setShowPaidInOut(false);
  };

  const handleNoSaleConfirm = (reason: string, note: string) => {
    const noSaleEntry = {
      reason,
      note,
      timestamp: toLocalISOString(),
      cashier: currentCashier,
    };
    const existing = JSON.parse(sessionStorage.getItem('noSaleEvents') || '[]');
    existing.push(noSaleEntry);
    sessionStorage.setItem('noSaleEvents', JSON.stringify(existing));

    appendDrawerEvent({
      type: 'no_sale',
      user: currentCashier,
      role: 'Cashier',
      reason,
      note,
    });

    setShowNoSale(false);
  };

  const { drawerInfo, events } = getDrawerDetailsForToday();


  return (
    <>
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-6 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center">
            <img src={logoUrl || "/ghorer-bazar-favicon.jpg"} alt={businessProfileName} className="h-14 w-32 object-cover rounded" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Drawer status pill hidden for now — may be revisited in the future.
          <CashDrawerStatusIndicator
            status={drawerInfo.status}
            onViewDetails={handleViewDrawerDetails}
          />
          */}

          <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
            <i className="ri-user-line text-lg"></i>
            <div className="text-left">
              <p className="text-xs text-gray-400 whitespace-nowrap">Cashier</p>
              <p className="text-sm font-semibold whitespace-nowrap">{currentCashier}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
            <i className="ri-store-line text-lg"></i>
            <div className="text-left">
              <p className="text-xs text-gray-400 whitespace-nowrap">{counterName}</p>
              <p className="text-sm font-semibold whitespace-nowrap">{outletName}</p>
            </div>
          </div>

          <div className="flex gap-2">
            {/* Paid In / Paid Out hidden for now — may be revisited in the future.
            <button
              onClick={handlePaidIn}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Paid In - Add cash to drawer"
            >
              <i className="ri-arrow-down-circle-line mr-1"></i>Paid In
            </button>
            <button
              onClick={handlePaidOut}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Paid Out - Remove cash from drawer"
            >
              <i className="ri-arrow-up-circle-line mr-1"></i>Paid Out
            </button>
            */}

            {/* Open Drawer hidden for now — may be revisited in the future.
            <button
              onClick={() => setShowOpenDrawer(true)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Open Cash Drawer"
            >
              <i className="ri-inbox-line mr-1"></i>
              Open Drawer
            </button>
            */}

            <button
              onClick={handleCheckOut}
              className="px-3 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Check Out - Close shift and reconcile cash"
            >
              <i className="ri-logout-box-line mr-1"></i>
              Check Out
            </button>
          </div>
        </div>
      </header>

      {showDrawerDetails && (
        <DrawerDetailsModal
          onClose={() => setShowDrawerDetails(false)}
          drawerInfo={drawerInfo}
          events={events}
          apiEvents={apiEvents}
          sessionFields={sessionFields}
          isLoading={sessionLoading}
        />
      )}

      {showNoSale && (
        <NoSaleModal
          onClose={() => setShowNoSale(false)}
          onConfirm={handleNoSaleConfirm}
          userRole="supervisor"
        />
      )}

      {showOpenDrawer && (
        <OpenDrawerModal
          onClose={() => setShowOpenDrawer(false)}
          onDrawerOpened={({ reason, note }) => {
            appendDrawerEvent({
              type: 'manual_open',
              user: currentCashier,
              role: 'Cashier',
              reason,
              note,
            });
          }}
        />
      )}

      {showPaidInOut && (
        <PaidInOutModal 
          type={paidInOutType}
          onClose={() => setShowPaidInOut(false)}
          onConfirm={handlePaidInOutConfirm}
          userRole="supervisor"
          currentBalance={paidInOutBalance}
        />
      )}

      {showChangeCashier && (
        <ChangeCashierModal 
          onClose={() => setShowChangeCashier(false)}
          currentCashier={currentCashier}
          onCashierChange={handleCashierChange}
        />
      )}

      {showClosingBalance && (
        <ClosingBalanceModal onClose={() => setShowClosingBalance(false)} />
      )}
    </>
  );
}
